import type { MouseEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip, { type ChipProps } from '@mui/material/Chip';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
    endsTextSelection,
    opensOutsideTheApp,
    repeatsClick,
    SELECTABLE_LINK_SX,
    stopDoubleClick
} from '../../components/routerLink';
import { splitLocationLabel } from './locationLabel';

interface FindingRefChipProps extends Omit<
    ChipProps,
    'label' | 'icon' | 'onClick'
> {
    /** The reference label — "Bef. 7000, Fl. 1, Pl. 1" (or just "Bef. 7000"). */
    label: string;
    /** Route to the referenced record's editor. Omitted → not a link. */
    to?: string;
    /**
     * Navigate handler (e.g. a guarded navigate from an editor). When omitted the
     * chip navigates directly — fine outside an editor, e.g. a grid cell.
     */
    onOpen?: (to: string) => void;
}

/**
 * A Befund/description reference as a chip that links to its editor. Reused by
 * the office grids (direct navigation) and the FindingDescriptionPicker
 * (guarded navigation + a delete handler). A leading marker icon flags that the
 * chip is a link; extra Chip props pass straight through.
 *
 * A linking chip renders as a real `<a href>` so it can be middle-clicked into
 * a new tab (see components/routerLink).
 */
export default function FindingRefChip({
    label,
    to,
    onOpen,
    onDelete,
    sx,
    ...chipProps
}: FindingRefChipProps) {
    const navigate = useNavigate();
    const handleClick = to
        ? (event: MouseEvent) => {
              // Don't let a grid cell / row swallow or double-handle the click.
              event.stopPropagation();
              // A new-tab click belongs to the browser — the chip is a real
              // link, so there is something for it to open.
              if (opensOutsideTheApp(event)) {
                  return;
              }
              event.preventDefault();
              if (repeatsClick(event) || endsTextSelection(event)) {
                  return;
              }
              if (onOpen) {
                  onOpen(to);
              } else {
                  navigate(to);
              }
          }
        : undefined;
    // As a link the chip is an <a>, and its delete X sits INSIDE that anchor.
    // MUI stops propagation on the X but never calls preventDefault — and
    // stopping propagation does NOT cancel a link's default action.
    const handleDelete = onDelete
        ? (event: MouseEvent) => {
              event.preventDefault();
              onDelete(event);
          }
        : undefined;
    // The Befundnummer set bold, the rest of the Lage line plain: down a column
    // of chips the number is what a reader scans for. A label that is not a
    // Lage line prints unchanged.
    const { before, number, after } = splitLocationLabel(label);
    return (
        <Chip
            size="small"
            variant="outlined"
            icon={to ? <PlaceOutlinedIcon /> : undefined}
            label={
                number ? (
                    <>
                        {before}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {number}
                        </Box>
                        {after}
                    </>
                ) : (
                    label
                )
            }
            {...chipProps}
            sx={[
                to ? SELECTABLE_LINK_SX : {},
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
            {...(to ? { component: RouterLink, to, draggable: false } : {})}
            clickable={Boolean(to)}
            onClick={handleClick}
            onDoubleClick={to ? stopDoubleClick : chipProps.onDoubleClick}
            onDelete={handleDelete}
        />
    );
}
